export enum PortalStatus {
  /** Cotação em Digitação - ProposalStatus: 10-Quotation In Progress */
  QuoteInProgress = 1,
  /** Cotação Encerrada - ProposalStatus: 4-Rejected */
  QuoteClosed = 2,
  /** Cotação Calculada - ProposalStatus: 10-Quotation In Progress */
  QuoteCalculated = 3,
  /** Cotação Expirada */
  QuoteExpired = 4,
  /** Cotação Excluída */
  QuoteDeleted = 5,
  /** Proposta em Digitação - ProposalStatus: 1-Entry */
  ProposalInProgress = 6,
  /** Proposta Aceita - ProposalStatus: 2-Bound */
  ProposalAccepted = 7,
  /** Proposta Transmitida */
  ProposalTransmitted = 8,
  /** Proposta Pendente Inspeção */
  ProposalPendingInspection = 9,
  /** Proposta Aprovada pela Inspeção */
  ProposalInspectionApproved = 10,
  /** Proposta Recusada pela Inspeção */
  ProposalInspectionRejected = 11,
  /** Proposta aguardando assinatura (Capital Global) */
  ProposalInSignature = 12,
  /** Proposta Expirada */
  ProposalExpired = 15,
  /** Cotação em Análise - ProposalStatus: 1-Not Effective */
  QuoteUnderAnalysis = 17,
  /** Proposta em Análise - ProposalStatus: 5-UnderWriting */
  ProposalInAnalysis = 18,
  /** Proposta Recusada pela Análise - ProposalStatus: 4-Rejected */
  ProposalRejectedByAnalysis = 19,
  /** Proposta cancelada pelo portal, antes de transmitir ou concluir a assinatura */
  ProposalCancelled = 21,
}

/**
 * Status em que a cotação deixou de ser uma oferta válida: não emite documento
 * novo (PDF/e-mail) nem segue para proposta.
 *
 * Espelha a deny-list da Api (`policies/quotes/domain/terminal-quote-status.ts`),
 * que responde 409 nesses status. Manter os dois lados alinhados evita botão
 * habilitado para uma ação que o backend recusa.
 *
 * `QuoteUnderAnalysis` (17) não entra: é transitório e tem bloqueio/copy próprios.
 */
const TERMINAL_QUOTE_STATUSES: readonly number[] = [
  PortalStatus.QuoteClosed,
  PortalStatus.QuoteExpired,
  PortalStatus.QuoteDeleted,
];

/** Rótulo curto do status terminal, para compor a mensagem de bloqueio. */
export const TERMINAL_QUOTE_STATUS_LABELS: Record<number, string> = {
  [PortalStatus.QuoteClosed]: 'encerrada',
  [PortalStatus.QuoteExpired]: 'expirada',
  [PortalStatus.QuoteDeleted]: 'excluída',
};

export function isTerminalQuoteStatus(
  statusPortal: string | number | undefined | null
): boolean {
  if (statusPortal == null || statusPortal === '') return false;

  const status = Number(statusPortal);
  if (!Number.isFinite(status)) return false;

  return TERMINAL_QUOTE_STATUSES.includes(status);
}

/**
 * Status em que a PROPOSTA está encerrada: expirada, recusada pela inspeção,
 * recusada pela análise ou cancelada. É o mesmo agrupamento que a listagem de
 * propostas exibe como "Encerrada" (ver `proposal-status-groups.ts`).
 *
 * Proposta nesses status não volta a ser editável por nenhum caminho: nem pelo
 * wizard, nem pela navegação entre steps, nem pelo "Voltar" dos resumos. Uma
 * nova proposta é o único caminho adiante.
 */
const TERMINAL_PROPOSAL_STATUSES: readonly number[] = [
  PortalStatus.ProposalInspectionRejected,
  PortalStatus.ProposalExpired,
  PortalStatus.ProposalRejectedByAnalysis,
  PortalStatus.ProposalCancelled,
];

/** Rótulo curto do status terminal da proposta, para compor mensagens. */
export const TERMINAL_PROPOSAL_STATUS_LABELS: Record<number, string> = {
  [PortalStatus.ProposalInspectionRejected]: 'recusada pela inspeção',
  [PortalStatus.ProposalExpired]: 'expirada',
  [PortalStatus.ProposalRejectedByAnalysis]: 'recusada pela análise',
  [PortalStatus.ProposalCancelled]: 'cancelada',
};

export function isTerminalProposalStatus(
  statusPortal: string | number | undefined | null
): boolean {
  if (statusPortal == null || statusPortal === '') return false;

  const status = Number(statusPortal);
  if (!Number.isFinite(status)) return false;

  return TERMINAL_PROPOSAL_STATUSES.includes(status);
}

/**
 * Cotação OU proposta encerrada. Os dois conjuntos de códigos não se cruzam
 * (2/4/5 são de cotação, 11/15/19/21 de proposta), então telas que servem aos
 * dois fluxos podem perguntar uma coisa só.
 */
export function isTerminalPortalStatus(
  statusPortal: string | number | undefined | null
): boolean {
  return (
    isTerminalQuoteStatus(statusPortal) ||
    isTerminalProposalStatus(statusPortal)
  );
}

/**
 * A apólice PME já virou proposta? Mesma regra do backend
 * (`isGroupLifeProposalPhase`): do status 6 ao 21, exceto 16 e 17.
 * 17 continua sendo cotação em análise.
 */
export function isGroupLifeProposalPhase(
  statusPortal: string | number | undefined | null
): boolean {
  if (statusPortal == null || statusPortal === '') return false;
  const status = Number(statusPortal);
  if (!Number.isFinite(status)) return false;
  return (
    status >= PortalStatus.ProposalInProgress &&
    status <= PortalStatus.ProposalCancelled &&
    status !== 16 &&
    status !== PortalStatus.QuoteUnderAnalysis
  );
}

/** Cotação PME pode ser clonada em qualquer status que ainda não seja proposta. */
export function canCloneGroupLifeQuote(
  statusPortal: string | number | undefined | null
): boolean {
  if (statusPortal == null || statusPortal === '') return false;
  const status = Number(statusPortal);
  if (!Number.isFinite(status) || status <= 0) return false;
  return !isGroupLifeProposalPhase(status);
}

/**
 * Proposta PME pode ser clonada em qualquer status da fase de proposta,
 * inclusive cancelada. Some quando já foi transmitida ou quando os dois
 * envelopes (proposta e carta comercial) estão concluídos.
 */
export function canCloneGroupLifeProposal(
  statusPortal: string | number | undefined | null,
  options?: { allSignaturesCompleted?: boolean }
): boolean {
  if (statusPortal == null || statusPortal === '') return false;
  const status = Number(statusPortal);
  if (!Number.isFinite(status) || status <= 0) return false;
  if (!isGroupLifeProposalPhase(status)) return false;
  if (status === PortalStatus.ProposalTransmitted) return false;
  if (options?.allSignaturesCompleted) return false;
  return true;
}

/**
 * Labels canônicos por `statusPortal`, exibidos nos resumos de proposta.
 * Telas com copy próprio para algum status (ex.: listagem de propostas,
 * resumo do capital global) aplicam override local em cima deste map.
 */
export const PORTAL_STATUS_LABELS: Record<number, string> = {
  [PortalStatus.ProposalInProgress]: 'Em digitação',
  [PortalStatus.ProposalInSignature]: 'Em assinatura',
  [PortalStatus.ProposalAccepted]: 'Efetivada',
  [PortalStatus.ProposalTransmitted]: 'Transmitida',
  [PortalStatus.ProposalPendingInspection]: 'Pendente inspeção',
  [PortalStatus.ProposalInspectionApproved]: 'Aguardando emissão',
  [PortalStatus.ProposalInspectionRejected]: 'Proposta recusada',
  [PortalStatus.ProposalExpired]: 'Proposta expirada',
  [PortalStatus.QuoteUnderAnalysis]: 'Cotação em Análise',
  [PortalStatus.ProposalInAnalysis]: 'Proposta em Análise',
  [PortalStatus.ProposalRejectedByAnalysis]: 'Proposta Recusada pela Análise',
  [PortalStatus.ProposalCancelled]: 'Proposta cancelada',
};
